import { ChooseMember } from 'app/src/components/ChooseMember';
import PopOver from 'app/scripts/views/lib/pop-over.coffee';
import React from 'react';
import Util from 'app/scripts/lib/util/index.coffee';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { AdvancedChecklistsBetaInfo } from './advanced-checklists-beta-info';

function bind<T extends (...args: unknown[]) => unknown>(
  fn: T,
  context: unknown,
): T {
  return fn.bind(context);
}

interface OptionsV2 {
  title: string;
  trackingMethod: string;
  getCardMembers: (this: unknown) => Member[];
  getBoardMembers: (this: unknown) => Member[];
  getInitialMember: () => string | undefined;
  setMember: (value: string | null) => void;
}

export function toggleAssignPopoverV2(options: OptionsV2) {
  const { title, trackingMethod } = options;

  return function toggle(e: MouseEvent) {
    Util.stop(e);

    const setMember = bind(options.setMember, this);
    const getInitialMember = bind(options.getInitialMember, this);

    const getCardMembers = bind(options.getCardMembers, this);
    const getBoardMembers = bind(options.getBoardMembers, this);

    const idMemberInitial = getInitialMember();

    trackUe(
      Noun.CARD_DETAIL,
      Verb.OPENS,
      idMemberInitial
        ? Noun.CHANGE_MEMBER_DIALOGUE
        : Noun.ASSIGN_MEMBER_DIALOGUE,
      'on checkitem',
      '',
      trackingMethod,
    );

    PopOver.toggle({
      elem: e.currentTarget,
      getViewTitle: () => title,
      keepEdits: true,
      reactElement: (
        <React.Fragment key="choose-member">
          <AdvancedChecklistsBetaInfo />
          <ChooseMember
            {...{
              idMemberInitial,
              getCardMembers,
              getBoardMembers,
            }}
            onSelect={(member) => {
              PopOver.hide();

              if (member && member.id !== idMemberInitial) {
                setMember(member.id);
              } else if (!member && idMemberInitial) {
                setMember(null);
              }
            }}
          />
        </React.Fragment>
      ),
    });
  };
}

interface Member {
  id: string;
  fullName: string;
  username: string;
}

interface OptionsFunctions {
  getCardMembers: (this: unknown) => Member[];
  getBoardMembers: (this: unknown) => Member[];
  getInitialValue: () => string;
  setValue: (value: string) => void;
  trackingMethod: string;
}

interface Options extends OptionsFunctions {
  title: string;
}

export function toggleAssignPopoverBeta(options: Options) {
  const { title, trackingMethod } = options;

  return function toggle(e: MouseEvent) {
    Util.stop(e);

    const setValue = bind(options.setValue, this);
    const getInitialValue = bind(options.getInitialValue, this);

    const getCardMembers = bind(options.getCardMembers, this);
    const getBoardMembers = bind(options.getBoardMembers, this);

    const initialValue = getInitialValue();
    const usernameMatch = initialValue.match(/^@[a-z0-9]{3,}/);
    // Clip @ off the front
    const existingUsername = usernameMatch && usernameMatch[0].substr(1);
    const existingMember =
      existingUsername &&
      getBoardMembers().find((member) => member.username === existingUsername);
    const idMemberInitial = existingMember ? existingMember.id : undefined;

    trackUe(
      Noun.CARD_DETAIL,
      Verb.OPENS,
      existingMember
        ? Noun.CHANGE_MEMBER_DIALOGUE
        : Noun.ASSIGN_MEMBER_DIALOGUE,
      'on checkitem',
      '',
      trackingMethod,
    );

    PopOver.toggle({
      elem: e.currentTarget,
      getViewTitle: () => title,
      keepEdits: true,
      reactElement: (
        <React.Fragment key="choose-member">
          <AdvancedChecklistsBetaInfo />
          <ChooseMember
            {...{
              idMemberInitial,
              getCardMembers,
              getBoardMembers,
            }}
            onSelect={(member) => {
              PopOver.hide();

              if (
                (member && member.id === idMemberInitial) ||
                (!member && !idMemberInitial)
              ) {
                return;
              }

              const mention = member
                ? /^\s/.test(initialValue)
                  ? `@${member.username}`
                  : `@${member.username} `
                : '';

              const newValue = idMemberInitial
                ? initialValue.replace(/^@[a-z0-9_]{3,}\s?|^/, mention)
                : mention + initialValue;

              setValue(newValue);
            }}
          />
        </React.Fragment>
      ),
    });
  };
}

export function replaceAssignInputValue(
  input: HTMLInputElement | HTMLTextAreaElement,
  newValue: string,
) {
  const initialValue = input.value;
  const delta = newValue.length - initialValue.length;
  const selectionStart = input.selectionStart;
  const selectionEnd = input.selectionEnd;

  input.value = newValue;

  if (selectionStart !== null && selectionEnd !== null) {
    const newSelectionStart =
      selectionStart === 0 &&
      selectionEnd === initialValue.length &&
      selectionEnd > selectionStart
        ? // If they've got everything selected, keep everything selected
          0
        : Math.max(0, selectionStart + delta);

    const newSelectionEnd = selectionEnd + delta;
    input.setSelectionRange(newSelectionStart, newSelectionEnd);
  }

  input.focus();
}
